import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, SimpleLoading, Text } from "@core/index";
import { ArrowLeft, Clock, Link2, Copy, ExternalLink } from "lucide-react";
import { useGet as useGetSpokenDialogue } from "@packages/api/madlenAPI";
import ErrorComponent from "@features/tools/components/error-component";

const DetailSpokenDialogueView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [, setCopying] = useState(false);

  const { data, isLoading, error } = useGetSpokenDialogue(Number(id), {
    query: {
      enabled: !!id,
      refetchInterval: (data: any) => {
        const s = (data as any)?.status as string | undefined;
        if (s === "READY" || s === "FAILED") return false;
        return 5000;
      },
      refetchOnWindowFocus: false,
    },
  });

  const formatRelativeTime = (input?: string | number | Date) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diffSeconds) >= sec || unit === "second") {
        const value = Math.round(diffSeconds / sec);
        return rtf.format(value, unit);
      }
    }
    return "";
  };

  if (isLoading) {
    return <SimpleLoading fastLoading />;
  }

  if (error || !data) {
    return (
      <ErrorComponent
        title={t("common.error")}
        description={t("common.error")}
      />
    );
  }

  const detail = (data as any) || {};
  const title = detail?.title || detail?.scenario || "";
  const description = detail?.description || "";
  const scenario = detail?.scenario || "";
  const language = detail?.language || "en";
  const accent = detail?.accent || "AMERICAN";
  const duration = detail?.duration || 6;
  const cefrLevel = detail?.cefrLevel || "";
  // removed weights since it's unused
  const status = (detail?.status as string | undefined) || undefined;
  const publicCode = detail?.publicCode;
  const imageUrl = detail?.imageUrl;
  const createdAt = detail?.createdAt;

  const publicUrl = publicCode
    ? `${window.location.origin}/content/spoken-dialogue/public/${publicCode}`
    : "";

  const statusColor = (status?: string) => {
    switch (status) {
      case "READY":
        return "text-green-600 bg-green-100 dark:text-green-300 dark:bg-green-900/30";
      case "PROCESSING":
        return "text-orange-600 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/30";
      case "FAILED":
        return "text-red-600 bg-red-100 dark:text-red-300 dark:bg-red-900/30";
      default:
        return "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-900/30";
    }
  };

  const statusText = (status?: string) => {
    switch (status) {
      case "READY":
        return t("spokenDialogue.status.ready");
      case "PROCESSING":
        return t("spokenDialogue.status.processing");
      case "FAILED":
        return t("spokenDialogue.status.failed");
      default:
        return t("spokenDialogue.status.unknown");
    }
  };

  return (
    <div className="flex-1 p-4 bg-gray-50 dark:bg-dark-500 overflow-y-auto h-full">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate("/content/spoken-dialogue")}
            size="md"
            rounded="full"
            variant="outline"
            leftIcon={<ArrowLeft size={18} />}
          >
            {t("common.back")}
          </Button>
        </div>

        {/* Header Card with Title and Description */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mb-4">
          <div className="flex items-start gap-4">
            {imageUrl && (
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <img
                  src={imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Text variant="h1" className="text-2xl font-semibold mb-2">
                    {title && String(title).trim().length > 0
                      ? title
                      : t("spokenDialogue.create.basicInfo.titlePlaceholder")}
                  </Text>
                  {description && (
                    <Text
                      variant="p"
                      className="text-gray-600 dark:text-gray-300 mb-2"
                    >
                      {description}
                    </Text>
                  )}
                  {scenario && (
                    <Text
                      variant="p"
                      className="text-gray-600 dark:text-gray-300 text-sm"
                    >
                      <span className="font-medium">Scenario:</span> {scenario}
                    </Text>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {status && (
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor(status)}`}
                    >
                      {status === "PROCESSING" && (
                        <div className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                      )}
                      {statusText(status)}
                    </span>
                  )}
                  {publicCode && (
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      Code: {publicCode}
                    </Text>
                  )}
                </div>
              </div>
              {createdAt && (
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {formatRelativeTime(createdAt)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Details (single column to make Settings full width) */}
        <div className="grid grid-cols-1 gap-4 mb-4">
          {/* Language & Settings (expanded) */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {t("interactive_material.create.contentLanguage")}:
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {language === "en" &&
                      t("interactive_material.create.contentLanguages.en")}
                    {language === "tr" &&
                      t("interactive_material.create.contentLanguages.tr")}
                    {language === "de" &&
                      t("interactive_material.create.contentLanguages.de")}
                    {language === "fr" &&
                      t("interactive_material.create.contentLanguages.fr")}
                    {language === "it" &&
                      t("interactive_material.create.contentLanguages.it")}
                    {language === "es" &&
                      t("interactive_material.create.contentLanguages.es")}
                  </span>
                  {accent && language === "en" && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {accent === "BRITISH" &&
                          t("spokenDialogue.detail.accent.british", "British")}
                        {accent === "AMERICAN" &&
                          t(
                            "spokenDialogue.detail.accent.american",
                            "American",
                          )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {cefrLevel && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("spokenDialogue.detail.level")}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {cefrLevel}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t("spokenDialogue.detail.durationLabel")}
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {t("spokenDialogue.detail.duration.dialogs", {
                    count: duration,
                  })}
                </span>
              </div>

              {/* Move prompt/sample into settings to make it longer */}
              {detail?.livekitPrompt && (
                <div className="pt-4 border-t border-gray-100 dark:border-dark-400">
                  <Text
                    variant="p"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span className="font-medium">
                      {t("spokenDialogue.detail.livekitPrompt")}:{" "}
                    </span>
                    <span className="block text-gray-800 dark:text-gray-200 whitespace-pre-wrap mt-1">
                      {detail.livekitPrompt}
                    </span>
                  </Text>
                </div>
              )}
              {detail?.livekitSample && (
                <div>
                  <Text
                    variant="p"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span className="font-medium">
                      {t("spokenDialogue.detail.livekitSample")}:{" "}
                    </span>
                    <span className="block text-gray-800 dark:text-gray-200 whitespace-pre-wrap mt-1">
                      {detail.livekitSample}
                    </span>
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        {publicUrl && (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <Text variant="h2" className="text-lg font-semibold mb-3">
              {t("spokenDialogue.create.share.title")}
            </Text>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center justify-center">
                <Link2 className="w-4 h-4" />
              </div>
              <input
                type="text"
                readOnly
                value={publicUrl}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none dark:bg-dark-500 dark:border-dark-400 dark:text-white border-gray-300"
              />
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Copy className="w-4 h-4" />}
                onClick={async () => {
                  try {
                    setCopying(true);
                    await navigator.clipboard.writeText(publicUrl);
                  } finally {
                    setCopying(false);
                  }
                }}
              >
                {t("common.copy")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ExternalLink className="w-4 h-4" />}
                onClick={() => window.open(publicUrl, "_blank")}
              >
                {t("common.open")}
              </Button>
            </div>
          </div>
        )}

        {/* Resources removed (duplicates of info now inside Settings) */}
      </div>
    </div>
  );
};

export default DetailSpokenDialogueView;
